import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "@/lib/site";
import { Logo } from "./Logo";

const linkCls = ({ isActive }) =>
  `relative whitespace-nowrap px-2.5 py-2 text-[12px] font-bold uppercase tracking-wider transition-colors duration-200 ${isActive ? "text-[#00E5FF]" : "text-slate-200 hover:text-white"}`;

const slug = (l) => l.toLowerCase().replace(/[^a-z]+/g, "-");

export default function Header({ announcement }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50">
      {announcement && (
        <div data-testid="announcement-bar" className="bg-[#FF5722] px-4 py-1.5 text-center text-[11px] font-bold uppercase tracking-wider text-white">
          {announcement}
        </div>
      )}
      <div className={`border-b transition-colors duration-300 ${scrolled || open ? "border-white/10 bg-[#0A192F]/90 backdrop-blur-xl" : "border-transparent bg-[#0A192F]/40 backdrop-blur-md"}`}>
        <div className="mx-auto flex h-[70px] max-w-[1440px] items-center justify-between gap-4 px-5 sm:px-8">
          <Logo />
          <nav aria-label="Main" className="hidden items-center xl:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${slug(n.label)}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/players/register" data-testid="header-register-btn" className="hidden rounded-full bg-[#FF5722] px-5 py-2.5 text-[12px] font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#ff7a4f] sm:inline-flex">
              Player Registration
            </Link>
            <button
              type="button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="rounded-md border border-white/15 p-2 text-white transition-colors hover:border-[#00E5FF] xl:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open && (
          <nav aria-label="Mobile" data-testid="mobile-menu" className="max-h-[80vh] overflow-y-auto border-t border-white/10 bg-[#0A192F] px-5 pb-6 xl:hidden">
            {NAV.map((n, i) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${slug(n.label)}`}
                className={({ isActive }) => `flex items-center justify-between border-b border-white/5 py-3.5 font-display text-xl uppercase ${isActive ? "text-[#00E5FF]" : "text-white"}`}
              >
                {n.label}
                <span className="font-score text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
              </NavLink>
            ))}
            <Link to="/players/register" data-testid="mobile-register-btn" className="mt-5 flex justify-center rounded-full bg-[#FF5722] py-3 text-sm font-bold uppercase tracking-wider text-white">
              Player Registration
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}
